import { MultiSelectVehicleAssociationType } from '@cobliteam/common-types';

import {
  type DeviceFragment,
  DeviceGeneralStatus,
  type VehicleGroupDto,
} from '@cobliteam/api-lib/src/__generated__/graphql';

import type { IFiltersDef } from '@cobliteam/ui/components/DataTable';

import { isAssociated } from '../../utils';

const filterDeviceByAssociationStatus = (
  device: DeviceFragment,
  associationStatus: MultiSelectVehicleAssociationType
) => {
  const isDeviceAssociated = isAssociated(device);

  if (
    associationStatus ===
    MultiSelectVehicleAssociationType.ASSOCIATED_TO_VEHICLE
  ) {
    return isDeviceAssociated;
  }

  if (
    associationStatus ===
    MultiSelectVehicleAssociationType.NOT_ASSOCIATED_TO_VEHICLE
  ) {
    return (
      device.generalStatus === DeviceGeneralStatus.LosingData ||
      !isDeviceAssociated
    );
  }

  return false;
};

const filterDeviceByGeneralType = (
  device: DeviceFragment,
  devicesFilters: IFiltersDef[]
) => {
  const values =
    devicesFilters.find((filter) => filter.key === 'MultiSelectDeviceType')
      ?.value || [];
  return (
    values.length === 0 ||
    values.some((filter: string) => filter === device.generalType)
  );
};

const filterDeviceByVehicleGroup = (
  device: DeviceFragment,
  devicesFilters: IFiltersDef[]
) => {
  const vehicleGroupIds: VehicleGroupDto[] = devicesFilters.find(
    (filter) => filter.key === 'MultiSelectVehicleGroups'
  )?.value;
  if (vehicleGroupIds?.length === 0) return true;

  if (device.vehicle?.groups?.length && device.vehicle?.groups[0].id) {
    return vehicleGroupIds.find(
      (group) => group.id === device.vehicle?.groups[0].id
    );
  }

  return false;
};

const filterDeviceByStatus = (
  device: DeviceFragment,
  devicesFilters: IFiltersDef[]
) => {
  const status: [] = devicesFilters.find(
    (filter) => filter.key === 'MultiSelectDeviceStatus'
  )?.value;
  const { generalStatus } = device;
  if (status.length === 0) return true;
  if (generalStatus) return status.find((filter) => filter === generalStatus);
  return false;
};

const sortDevices = (deviceA: DeviceFragment, deviceB: DeviceFragment) => {
  const statusPriority = {
    [DeviceGeneralStatus.Damaged]: 0,
    [DeviceGeneralStatus.Unavailable]: 1,
    [DeviceGeneralStatus.Disconnected]: 2,
    [DeviceGeneralStatus.NoSignal]: 3,
    [DeviceGeneralStatus.Syncing]: 4,
    [DeviceGeneralStatus.Normal]: 5,
    [DeviceGeneralStatus.NoSufficientData]: 6,
    [DeviceGeneralStatus.LosingData]: 7,
  };

  const { generalStatus: aStatus } = deviceA;
  const { generalStatus: bStatus } = deviceB;

  return statusPriority[aStatus] - statusPriority[bStatus];
};

interface GetFilteredDeviceProps {
  devices: DeviceFragment[];
  devicesFilters: IFiltersDef[];
  associationStatus: MultiSelectVehicleAssociationType;
}

const getFilteredDevices = ({
  devices,
  devicesFilters,
  associationStatus,
}: GetFilteredDeviceProps) => {
  return devices
    .filter(
      (device) =>
        filterDeviceByAssociationStatus(device, associationStatus) &&
        filterDeviceByGeneralType(device, devicesFilters) &&
        filterDeviceByVehicleGroup(device, devicesFilters) &&
        filterDeviceByStatus(device, devicesFilters)
    )
    .sort(sortDevices);
};

export { filterDeviceByAssociationStatus, getFilteredDevices };
